import { createHash } from "crypto";

import type { AvailabilityAppointmentSlot, ScheduleAvailabilityOption } from "@/shared/agent-api/responses";
import { appointmentSlotSemanticKey } from "@/shared/scheduling/agent-availability-helpers";

const DEFAULT_OPTION_LIMIT = 10;

type InternallyRankedSlot = AvailabilityAppointmentSlot & { policyRank?: number };

function actionableSlot(slot: AvailabilityAppointmentSlot): slot is AvailabilityAppointmentSlot & {
  appointmentSlotActionId: string;
  practitionerId: string;
  locationId: string;
} {
  return Boolean(slot.appointmentSlotActionId && slot.practitionerId && slot.locationId);
}

function optionId(slot: AvailabilityAppointmentSlot): string {
  return `slot-${createHash("sha256").update(appointmentSlotSemanticKey(slot), "utf8").digest("base64url")}`;
}

function compareScheduleOptions(left: ScheduleAvailabilityOption, right: ScheduleAvailabilityOption): number {
  const leftPolicyRank = (left.slot as InternallyRankedSlot).policyRank;
  const rightPolicyRank = (right.slot as InternallyRankedSlot).policyRank;

  if ((leftPolicyRank === undefined) !== (rightPolicyRank === undefined)) {
    throw new Error("Cannot merge policy-ranked and chronologically ranked scheduling options");
  }

  const policyRankDifference = (leftPolicyRank ?? 0) - (rightPolicyRank ?? 0);

  if (policyRankDifference !== 0) return policyRankDifference;

  const startDifference = Date.parse(left.slot.start) - Date.parse(right.slot.start);

  if (startDifference !== 0) return startDifference;

  const endDifference = Date.parse(left.slot.end) - Date.parse(right.slot.end);

  if (endDifference !== 0) return endDifference;

  if (leftPolicyRank !== undefined) {
    return (
      left.slot.practitionerId.localeCompare(right.slot.practitionerId, "en", { numeric: true }) ||
      left.slot.locationId.localeCompare(right.slot.locationId, "en", { numeric: true }) ||
      left.slot.appointmentId.localeCompare(right.slot.appointmentId, "en", { numeric: true })
    );
  }

  return left.id.localeCompare(right.id, "en", { numeric: true });
}

/** Merge schedule-option sets through one canonical identity, ranking, and response limit. */
export function mergeScheduleOptionSets(
  optionSets: readonly (readonly ScheduleAvailabilityOption[])[],
  limit: number = DEFAULT_OPTION_LIMIT
): ScheduleAvailabilityOption[] {
  const byId = new Map<string, ScheduleAvailabilityOption>();

  for (const option of optionSets.flat()) {
    const existing = byId.get(option.id);

    // Equivalent openings can be returned with regenerated EHR slot IDs. Retain a deterministic
    // signed capability so recomposition is stable within one authoritative search page.
    if (
      existing === undefined ||
      option.slot.appointmentId.localeCompare(existing.slot.appointmentId, "en", { numeric: true }) < 0
    ) {
      byId.set(option.id, option);
    }
  }

  return [...byId.values()].sort(compareScheduleOptions).slice(0, limit);
}

/** Compose caller-selectable booking options only from complete, signed backend availability. */
export function composeScheduleOptions(
  slots: readonly AvailabilityAppointmentSlot[],
  limit: number = DEFAULT_OPTION_LIMIT
): ScheduleAvailabilityOption[] {
  const unsignedActionableSlot = slots.find(
    (slot) => slot.practitionerId !== null && slot.locationId !== null && slot.appointmentSlotActionId === undefined
  );

  if (unsignedActionableSlot) {
    throw new Error("Scheduling options require signed appointment-slot capabilities");
  }

  return mergeScheduleOptionSets(
    [slots.filter(actionableSlot).map((slot) => ({ kind: "slot" as const, id: optionId(slot), slot }))],
    limit
  );
}
